import { useCallback, useEffect, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { cn } from "@/lib/utils";
import { listLibraryChanges } from "@/apps/rag/api/rag-api.js";
import { formatRelativeTime } from "@/apps/rag/api/mappers.js";
import type { ApiLibraryChange } from "@/apps/rag/api/types.js";
import { ListPagination } from "./ListPagination.js";

const CHANGE_STYLES: Record<ApiLibraryChange["changeType"], string> = {
  ADDED: "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  UPDATED: "bg-amber-500/15 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  WITHDRAWN: "bg-rose-500/15 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
};

const CHANGE_LABELS: Record<ApiLibraryChange["changeType"], string> = {
  ADDED: "Added",
  UPDATED: "Updated",
  WITHDRAWN: "Withdrawn",
};

const FILE_CHANGE_FETCH_LIMIT = 100;

function matchesOpenDocument(change: ApiLibraryChange, documentId: string, documentTitle?: string) {
  if (change.documentId) {
    return change.documentId === documentId;
  }
  const title = documentTitle?.trim().toLowerCase();
  if (!title) return false;
  return (
    change.title.trim().toLowerCase() === title
    || (change.externalId?.trim().toLowerCase() ?? "") === title
  );
}

interface LibraryChangesListProps {
  libraryId: string;
  documentId?: string;
  documentTitle?: string;
  className?: string;
  onView: (documentId: string, title: string, documentVersionId?: string | null) => void;
}

export function LibraryChangesList({
  libraryId,
  documentId,
  documentTitle,
  className,
  onView,
}: LibraryChangesListProps) {
  const fileScoped = Boolean(documentId);
  const [changes, setChanges] = useState<ApiLibraryChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [libraryId, documentId, documentTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listLibraryChanges(libraryId, {
        page: fileScoped ? 1 : page,
        limit: fileScoped ? FILE_CHANGE_FETCH_LIMIT : limit,
        documentId,
      });
      const rows = fileScoped
        ? result.changes.filter((change) => matchesOpenDocument(change, documentId!, documentTitle))
        : result.changes;
      setChanges(rows);
      setTotal(fileScoped ? rows.length : result.total);
      const nextTotalPages = Math.max(1, Math.ceil((fileScoped ? rows.length : result.total) / limit));
      if (!fileScoped && page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load changes");
    } finally {
      setLoading(false);
    }
  }, [libraryId, documentId, documentTitle, fileScoped, page, limit]);

  useEffect(() => {
    if (fileScoped) return;
    void load();
  }, [fileScoped, load]);

  useEffect(() => {
    if (!fileScoped) return;
    void load();
  }, [fileScoped, libraryId, documentId, documentTitle]);

  useEffect(() => {
    if (!fileScoped) return;
    const nextTotalPages = Math.max(1, Math.ceil(changes.length / limit));
    if (page > nextTotalPages) {
      setPage(nextTotalPages);
    }
  }, [fileScoped, changes.length, limit, page]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/15 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  const visibleChanges = fileScoped ? changes.slice((page - 1) * limit, page * limit) : changes;

  if (changes.length === 0 && total === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
        {documentId ? "No recent changes for this file." : "No recent changes for this library."}
      </div>
    );
  }

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {visibleChanges.map((change) => {
          const canView =
            change.changeType !== "WITHDRAWN" && Boolean(change.documentId);
          const viewTitle = canView
            ? "View document"
            : change.changeType === "WITHDRAWN"
              ? "Document withdrawn"
              : "Document unavailable";

          return (
            <li
              key={change.id}
              className="rounded-xl border border-border bg-muted px-3.5 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${CHANGE_STYLES[change.changeType]}`}
                    >
                      {CHANGE_LABELS[change.changeType]}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {formatRelativeTime(change.changedAt)}
                    </span>
                  </div>
                  <p className="mt-1.5 truncate text-sm text-foreground" title={change.title}>
                    {change.title}
                  </p>
                  {change.externalId ? (
                    <p
                      className="mt-1 truncate font-mono text-[11px] text-muted-foreground"
                      title={change.externalId}
                    >
                      {change.externalId}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center">
                  <IconButton
                    type="button"
                    size="sm"
                    title={viewTitle}
                    aria-label={viewTitle}
                    disabled={!canView}
                    onClick={() => {
                      if (!change.documentId) return;
                      onView(change.documentId, change.title, change.documentVersionId);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </IconButton>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <ListPagination
        className="mt-auto shrink-0"
        page={page}
        limit={limit}
        total={total}
        loading={loading}
        onPageChange={setPage}
        onLimitChange={(nextLimit) => {
          setLimit(nextLimit);
          setPage(1);
        }}
      />
    </div>
  );
}
